import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Wrench, Plane, AlertTriangle, FileText, Clock, CheckCircle2, XCircle, 
  Plus, Search, RefreshCw, Eye, Edit, Trash2, Settings, Package,
  ClipboardList, AlertCircle, Calendar, BarChart3, Activity, Cog,
  Shield, Box, TrendingUp, FileWarning, Timer, Loader2, ChevronRight
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL;

export default function MaintenancePage() {
  const { t } = useTranslation();
  const token = localStorage.getItem('token');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Work Orders
  const [workOrders, setWorkOrders] = useState([]);
  const [showWorkOrderDialog, setShowWorkOrderDialog] = useState(false);
  const [workOrderForm, setWorkOrderForm] = useState({
    aircraft_id: '',
    title: '',
    description: '',
    work_order_type: 'unscheduled',
    priority: 'routine',
    tasks: []
  });
  
  // MEL Items
  const [melItems, setMelItems] = useState([]);
  const [showMELDialog, setShowMELDialog] = useState(false);
  
  // Defects
  const [defects, setDefects] = useState([]);
  const [showDefectDialog, setShowDefectDialog] = useState(false);
  const [defectForm, setDefectForm] = useState({
    aircraft_id: '',
    ata_chapter: '',
    title: '',
    description: '',
    severity: 'minor',
    source: 'pilot_report'
  });
  
  // Parts
  const [parts, setParts] = useState([]);
  const [showPartDialog, setShowPartDialog] = useState(false);
  
  // Aircraft list for dropdowns
  const [aircraft, setAircraft] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const apiHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // ==================== DATA FETCHING ====================
  
  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/maintenance/stats`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchWorkOrders = async () => {
    try {
      let url = `${API_URL}/api/maintenance/work-orders?limit=100`;
      if (searchQuery) url += `&search=${searchQuery}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      
      const res = await fetch(url, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setWorkOrders(data.work_orders || []);
      }
    } catch (err) {
      console.error('Error fetching work orders:', err);
    }
  };

  const fetchMELItems = async () => {
    try {
      const res = await fetch(`${API_URL}/api/maintenance/mel-items?limit=100`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setMelItems(data.mel_items || []);
      }
    } catch (err) {
      console.error('Error fetching MEL items:', err);
    }
  };

  const fetchDefects = async () => {
    try {
      let url = `${API_URL}/api/maintenance/defects?limit=100`;
      if (searchQuery) url += `&search=${searchQuery}`;
      
      const res = await fetch(url, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setDefects(data.defects || []);
      }
    } catch (err) {
      console.error('Error fetching defects:', err);
    }
  };

  const fetchParts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/maintenance/parts?limit=100`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setParts(data.parts || []);
      }
    } catch (err) {
      console.error('Error fetching parts:', err);
    }
  };

  const fetchAircraft = async () => {
    try {
      const res = await fetch(`${API_URL}/api/aircraft?limit=100`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setAircraft(data.aircraft || data || []);
      }
    } catch (err) {
      console.error('Error fetching aircraft:', err);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([
      fetchStats(),
      fetchWorkOrders(),
      fetchMELItems(),
      fetchDefects(),
      fetchParts(),
      fetchAircraft()
    ]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // ==================== ACTIONS ====================

  const seedSampleData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/maintenance/seed-sample-data`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchAll();
      } else {
        toast.error('Failed to seed sample data');
      }
    } catch (err) {
      toast.error('Error seeding data');
    }
  };

  const createWorkOrder = async () => {
    try {
      const res = await fetch(`${API_URL}/api/maintenance/work-orders`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify(workOrderForm)
      });
      if (res.ok) {
        toast.success('Work order created');
        setShowWorkOrderDialog(false);
        setWorkOrderForm({
          aircraft_id: '',
          title: '',
          description: '',
          work_order_type: 'unscheduled',
          priority: 'routine',
          tasks: []
        });
        fetchWorkOrders();
        fetchStats();
      } else {
        toast.error('Failed to create work order');
      }
    } catch (err) {
      toast.error('Error creating work order');
    }
  };

  const createDefect = async () => {
    try {
      const res = await fetch(`${API_URL}/api/maintenance/defects`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify(defectForm)
      });
      if (res.ok) {
        toast.success('Defect reported');
        setShowDefectDialog(false);
        setDefectForm({
          aircraft_id: '',
          ata_chapter: '',
          title: '',
          description: '',
          severity: 'minor',
          source: 'pilot_report'
        });
        fetchDefects();
        fetchStats();
      } else {
        toast.error('Failed to report defect');
      }
    } catch (err) {
      toast.error('Error reporting defect');
    }
  };

  const updateWorkOrderStatus = async (woId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/maintenance/work-orders/${woId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: apiHeaders
      });
      if (res.ok) {
        toast.success(`Work order status updated to ${newStatus}`);
        fetchWorkOrders();
        fetchStats();
      }
    } catch (err) {
      toast.error('Error updating status');
    }
  };

  // ==================== HELPERS ====================

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-500/20 text-gray-300',
      planned: 'bg-blue-500/20 text-blue-300',
      in_progress: 'bg-yellow-500/20 text-yellow-300',
      awaiting_parts: 'bg-orange-500/20 text-orange-300',
      completed: 'bg-green-500/20 text-green-300',
      released: 'bg-emerald-500/20 text-emerald-300',
      open: 'bg-red-500/20 text-red-300',
      rectified: 'bg-green-500/20 text-green-300',
      deferred: 'bg-purple-500/20 text-purple-300'
    };
    return colors[status] || 'bg-slate-500/20 text-slate-300';
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      aog: 'bg-red-600/30 text-red-300 border-red-500',
      critical: 'bg-red-500/20 text-red-300 border-red-500/50',
      high: 'bg-orange-500/20 text-orange-300 border-orange-500/50',
      routine: 'bg-blue-500/20 text-blue-300 border-blue-500/50',
      low: 'bg-slate-500/20 text-slate-300 border-slate-500/50'
    };
    return colors[priority] || 'bg-slate-500/20 text-slate-300';
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      aog: 'bg-red-600/30 text-red-300',
      safety: 'bg-red-500/20 text-red-300',
      major: 'bg-orange-500/20 text-orange-300',
      minor: 'bg-yellow-500/20 text-yellow-300',
      cosmetic: 'bg-slate-500/20 text-slate-300'
    };
    return colors[severity] || 'bg-slate-500/20 text-slate-300';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 p-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
        <span className="ml-3 text-slate-400">Loading maintenance data...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-6" data-testid="maintenance-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Wrench className="w-8 h-8 text-orange-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">{t('maintenance.title')}</h1>
            <p className="text-slate-400">{t('maintenance.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={fetchAll} className="border-slate-600">
            <RefreshCw className="w-4 h-4 mr-2" /> {t('common.refresh')}
          </Button>
          <Button onClick={seedSampleData} className="bg-purple-500 hover:bg-purple-600">
            <Package className="w-4 h-4 mr-2" /> Seed Sample Data
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">{t('maintenance.workOrders')}</p>
                  <p className="text-2xl font-bold text-white">{stats.work_orders.total}</p>
                </div>
                <ClipboardList className="w-8 h-8 text-blue-400 opacity-70" />
              </div>
              <p className="text-xs text-slate-500 mt-1">{stats.work_orders.in_progress} {t('common.inProgress').toLowerCase()}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Active MEL</p>
                  <p className="text-2xl font-bold text-yellow-400">{stats.mel_cdl.active}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-yellow-400 opacity-70" />
              </div>
              <p className="text-xs text-red-400 mt-1">{stats.mel_cdl.expiring_soon} expiring soon</p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Open Defects</p>
                  <p className="text-2xl font-bold text-orange-400">{stats.defects.open}</p>
                </div>
                <FileWarning className="w-8 h-8 text-orange-400 opacity-70" />
              </div>
              <p className="text-xs text-red-400 mt-1">{stats.defects.aog} AOG</p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Open ADs</p>
                  <p className="text-2xl font-bold text-red-400">{stats.ad_sb.open_ads}</p>
                </div>
                <Shield className="w-8 h-8 text-red-400 opacity-70" />
              </div>
              <p className="text-xs text-slate-500 mt-1">{stats.ad_sb.upcoming_due} due soon</p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Low Stock Parts</p>
                  <p className="text-2xl font-bold text-purple-400">{stats.parts.low_stock}</p>
                </div>
                <Box className="w-8 h-8 text-purple-400 opacity-70" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Fleet Availability</p>
                  <p className="text-2xl font-bold text-emerald-400">{stats.fleet.availability_percent}%</p>
                </div>
                <Plane className="w-8 h-8 text-emerald-400 opacity-70" />
              </div>
              <p className="text-xs text-slate-500 mt-1">{stats.fleet.total_aircraft} aircraft</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50 mb-6">
          <TabsTrigger value="dashboard" data-testid="maintenance-dashboard-tab">
            <BarChart3 className="w-4 h-4 mr-2" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="work-orders" data-testid="work-orders-tab">
            <ClipboardList className="w-4 h-4 mr-2" /> Work Orders
          </TabsTrigger>
          <TabsTrigger value="mel-cdl" data-testid="mel-cdl-tab">
            <AlertTriangle className="w-4 h-4 mr-2" /> MEL/CDL
          </TabsTrigger>
          <TabsTrigger value="defects" data-testid="defects-tab">
            <FileWarning className="w-4 h-4 mr-2" /> Defects
          </TabsTrigger>
          <TabsTrigger value="parts" data-testid="parts-tab">
            <Box className="w-4 h-4 mr-2" /> Parts Inventory
          </TabsTrigger>
          <TabsTrigger value="ad-sb" data-testid="ad-sb-tab">
            <Shield className="w-4 h-4 mr-2" /> AD/SB Tracking
          </TabsTrigger>
        </TabsList>

        {/* DASHBOARD TAB */}
        <TabsContent value="dashboard" data-testid="maintenance-dashboard-content">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Work Orders */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-blue-400" />
                  Recent Work Orders
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {workOrders.slice(0, 5).map(wo => (
                    <div key={wo.id} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                      <div>
                        <p className="font-medium text-white">{wo.work_order_number}</p>
                        <p className="text-sm text-slate-400">{wo.title}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getPriorityBadge(wo.priority)}>{wo.priority}</Badge>
                        <Badge className={getStatusBadge(wo.status)}>{wo.status?.replace('_', ' ')}</Badge>
                      </div>
                    </div>
                  ))}
                  {workOrders.length === 0 && (
                    <p className="text-slate-500 text-center py-4">No work orders yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Active MEL Items */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  Active MEL Items
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {melItems.filter(m => m.status === 'open' || m.status === 'extended').slice(0, 5).map(mel => (
                    <div key={mel.id} className={`flex items-center justify-between p-3 rounded-lg ${
                      mel.days_remaining <= 1 ? 'bg-red-500/10 border border-red-500/30' :
                      mel.days_remaining <= 3 ? 'bg-yellow-500/10 border border-yellow-500/30' :
                      'bg-slate-900/50'
                    }`}>
                      <div>
                        <p className="font-medium text-white">{mel.mel_number}</p>
                        <p className="text-sm text-slate-400">{mel.title}</p>
                      </div>
                      <div className="text-right">
                        <Badge className={`bg-${mel.category === 'A' ? 'red' : mel.category === 'B' ? 'orange' : mel.category === 'C' ? 'yellow' : 'blue'}-500/20 text-${mel.category === 'A' ? 'red' : mel.category === 'B' ? 'orange' : mel.category === 'C' ? 'yellow' : 'blue'}-300`}>
                          Cat {mel.category}
                        </Badge>
                        <p className={`text-sm mt-1 ${mel.days_remaining <= 1 ? 'text-red-400' : mel.days_remaining <= 3 ? 'text-yellow-400' : 'text-slate-400'}`}>
                          {mel.days_remaining} days left
                        </p>
                      </div>
                    </div>
                  ))}
                  {melItems.filter(m => m.status === 'open').length === 0 && (
                    <p className="text-slate-500 text-center py-4">No active MEL items</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Open Defects */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileWarning className="w-5 h-5 text-orange-400" />
                  Open Defects
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {defects.filter(d => d.status === 'open').slice(0, 5).map(defect => (
                    <div key={defect.id} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                      <div>
                        <p className="font-medium text-white">{defect.defect_number}</p>
                        <p className="text-sm text-slate-400">{defect.title}</p>
                        <p className="text-xs text-slate-500">ATA {defect.ata_chapter} • {defect.aircraft_registration}</p>
                      </div>
                      <Badge className={getSeverityBadge(defect.severity)}>{defect.severity}</Badge>
                    </div>
                  ))}
                  {defects.filter(d => d.status === 'open').length === 0 && (
                    <p className="text-slate-500 text-center py-4">No open defects</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-green-400" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2 bg-slate-900/50 border-slate-600 hover:bg-slate-800"
                    onClick={() => setShowWorkOrderDialog(true)}
                  >
                    <ClipboardList className="w-5 h-5 text-blue-400" />
                    <span className="text-sm">New Work Order</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2 bg-slate-900/50 border-slate-600 hover:bg-slate-800"
                    onClick={() => setShowDefectDialog(true)}
                  >
                    <FileWarning className="w-5 h-5 text-orange-400" />
                    <span className="text-sm">Report Defect</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2 bg-slate-900/50 border-slate-600 hover:bg-slate-800"
                    onClick={() => setShowMELDialog(true)}
                  >
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                    <span className="text-sm">New MEL Deferral</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2 bg-slate-900/50 border-slate-600 hover:bg-slate-800"
                    onClick={() => setShowPartDialog(true)}
                  >
                    <Box className="w-5 h-5 text-purple-400" />
                    <span className="text-sm">Add Part</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* WORK ORDERS TAB */}
        <TabsContent value="work-orders" data-testid="work-orders-content">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Work Orders</CardTitle>
                <CardDescription>Track and manage maintenance work orders</CardDescription>
              </div>
              <Button onClick={() => setShowWorkOrderDialog(true)} className="bg-blue-500 hover:bg-blue-600">
                <Plus className="w-4 h-4 mr-2" /> New Work Order
              </Button>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search work orders..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-900 border-slate-600"
                  />
                </div>
                <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-40 bg-slate-900 border-slate-600">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="planned">Planned</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="awaiting_parts">Awaiting Parts</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="released">Released</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={fetchWorkOrders} className="border-slate-600">
                  <Search className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-3">
                {workOrders.map(wo => (
                  <div key={wo.id} className="p-4 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-blue-400">{wo.work_order_number}</span>
                          <Badge className={getPriorityBadge(wo.priority)}>{wo.priority?.toUpperCase()}</Badge>
                          <Badge className={getStatusBadge(wo.status)}>{wo.status?.replace('_', ' ')}</Badge>
                          <Badge variant="outline" className="border-slate-600 text-slate-400">{wo.work_order_type?.replace('_', ' ')}</Badge>
                        </div>
                        <h3 className="font-medium text-white mb-1">{wo.title}</h3>
                        <p className="text-sm text-slate-400 mb-2">{wo.description}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Plane className="w-3 h-3" /> {wo.aircraft_registration || 'No Aircraft'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> Est: {wo.total_estimated_hours}h
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> {wo.created_at ? new Date(wo.created_at).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {wo.status === 'draft' && (
                          <Button size="sm" variant="outline" onClick={() => updateWorkOrderStatus(wo.id, 'planned')}>
                            Start Planning
                          </Button>
                        )}
                        {wo.status === 'planned' && (
                          <Button size="sm" variant="outline" onClick={() => updateWorkOrderStatus(wo.id, 'in_progress')} className="bg-yellow-500/20 border-yellow-500/50">
                            Start Work
                          </Button>
                        )}
                        {wo.status === 'in_progress' && (
                          <Button size="sm" variant="outline" onClick={() => updateWorkOrderStatus(wo.id, 'completed')} className="bg-green-500/20 border-green-500/50">
                            Complete
                          </Button>
                        )}
                        <Button size="sm" variant="ghost">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {workOrders.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No work orders found</p>
                    <p className="text-sm">Create a new work order or seed sample data</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* MEL/CDL TAB */}
        <TabsContent value="mel-cdl" data-testid="mel-cdl-content">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>MEL/CDL Items</CardTitle>
                <CardDescription>Minimum Equipment List & Configuration Deviation List deferrals</CardDescription>
              </div>
              <Button onClick={() => setShowMELDialog(true)} className="bg-yellow-500 hover:bg-yellow-600">
                <Plus className="w-4 h-4 mr-2" /> New MEL Item
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {melItems.map(mel => (
                  <div key={mel.id} className={`p-4 rounded-lg border transition-colors ${
                    mel.status === 'open' && mel.days_remaining <= 1 ? 'bg-red-500/10 border-red-500/30' :
                    mel.status === 'open' && mel.days_remaining <= 3 ? 'bg-yellow-500/10 border-yellow-500/30' :
                    mel.status === 'rectified' ? 'bg-green-500/10 border-green-500/30' :
                    'bg-slate-900/50 border-slate-700'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-yellow-400">{mel.mel_number}</span>
                          <Badge className={`bg-${mel.category === 'A' ? 'red' : mel.category === 'B' ? 'orange' : mel.category === 'C' ? 'yellow' : 'blue'}-500/20`}>
                            Category {mel.category}
                          </Badge>
                          <Badge className={getStatusBadge(mel.status)}>{mel.status}</Badge>
                        </div>
                        <h3 className="font-medium text-white mb-1">{mel.title}</h3>
                        <p className="text-sm text-slate-400 mb-2">{mel.description}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>ATA {mel.ata_chapter}</span>
                          <span>{mel.aircraft_registration}</span>
                          <span>Item: {mel.item_number}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        {mel.status === 'open' && (
                          <div className={`text-lg font-bold ${mel.days_remaining <= 1 ? 'text-red-400' : mel.days_remaining <= 3 ? 'text-yellow-400' : 'text-slate-300'}`}>
                            {mel.days_remaining} days
                          </div>
                        )}
                        <p className="text-xs text-slate-500">Expires: {mel.expiry_date ? new Date(mel.expiry_date).toLocaleDateString() : 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {melItems.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No MEL/CDL items found</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* DEFECTS TAB */}
        <TabsContent value="defects" data-testid="defects-content">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Defect Tracking</CardTitle>
                <CardDescription>Report and track aircraft defects and discrepancies</CardDescription>
              </div>
              <Button onClick={() => setShowDefectDialog(true)} className="bg-orange-500 hover:bg-orange-600">
                <Plus className="w-4 h-4 mr-2" /> Report Defect
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {defects.map(defect => (
                  <div key={defect.id} className={`p-4 rounded-lg border transition-colors ${
                    defect.severity === 'aog' ? 'bg-red-500/10 border-red-500/30' :
                    defect.severity === 'safety' ? 'bg-orange-500/10 border-orange-500/30' :
                    'bg-slate-900/50 border-slate-700'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-orange-400">{defect.defect_number}</span>
                          <Badge className={getSeverityBadge(defect.severity)}>{defect.severity?.toUpperCase()}</Badge>
                          <Badge className={getStatusBadge(defect.status)}>{defect.status}</Badge>
                        </div>
                        <h3 className="font-medium text-white mb-1">{defect.title}</h3>
                        <p className="text-sm text-slate-400 mb-2">{defect.description}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>ATA {defect.ata_chapter}</span>
                          <span>{defect.aircraft_registration}</span>
                          <span>Source: {defect.source?.replace('_', ' ')}</span>
                          <span>{defect.reported_at ? new Date(defect.reported_at).toLocaleDateString() : ''}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {defect.status === 'open' && defect.can_defer && (
                          <Button size="sm" variant="outline" className="border-yellow-500/50 text-yellow-400">
                            Defer (MEL)
                          </Button>
                        )}
                        <Button size="sm" variant="ghost">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {defects.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <FileWarning className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No defects found</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PARTS INVENTORY TAB */}
        <TabsContent value="parts" data-testid="parts-content">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Parts Inventory</CardTitle>
                <CardDescription>Track rotables, expendables, and consumables</CardDescription>
              </div>
              <Button onClick={() => setShowPartDialog(true)} className="bg-purple-500 hover:bg-purple-600">
                <Plus className="w-4 h-4 mr-2" /> Add Part
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {parts.map(part => (
                  <div key={part.id} className={`p-4 rounded-lg border transition-colors ${
                    part.quantity_on_hand <= part.reorder_point ? 'bg-red-500/10 border-red-500/30' :
                    'bg-slate-900/50 border-slate-700'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-purple-400">{part.part_number}</span>
                          <Badge className="bg-slate-500/20 text-slate-300">{part.category}</Badge>
                          <Badge className={`${part.condition === 'new' ? 'bg-green-500/20 text-green-300' : part.condition === 'overhauled' ? 'bg-blue-500/20 text-blue-300' : 'bg-yellow-500/20 text-yellow-300'}`}>
                            {part.condition}
                          </Badge>
                        </div>
                        <h3 className="font-medium text-white mb-1">{part.description}</h3>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>Mfg: {part.manufacturer || 'N/A'}</span>
                          {part.serial_number && <span>S/N: {part.serial_number}</span>}
                          <span>Location: {part.location || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${part.quantity_on_hand <= part.reorder_point ? 'text-red-400' : 'text-white'}`}>
                          Qty: {part.quantity_on_hand}
                        </p>
                        <p className="text-sm text-slate-400">${part.unit_cost?.toLocaleString()}</p>
                        {part.quantity_on_hand <= part.reorder_point && (
                          <p className="text-xs text-red-400">Below reorder point</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {parts.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <Box className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No parts in inventory</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AD/SB TAB */}
        <TabsContent value="ad-sb" data-testid="ad-sb-content">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle>AD/SB Compliance Tracking</CardTitle>
              <CardDescription>Track Airworthiness Directives and Service Bulletins</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-slate-500">
                <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>AD/SB tracking module</p>
                <p className="text-sm">Create AD records and track fleet compliance</p>
                <Button variant="outline" className="mt-4 border-slate-600">
                  <Plus className="w-4 h-4 mr-2" /> Add AD Record
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* CREATE WORK ORDER DIALOG */}
      <Dialog open={showWorkOrderDialog} onOpenChange={setShowWorkOrderDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-400" />
              Create Work Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Aircraft</Label>
              <Select 
                value={workOrderForm.aircraft_id} 
                onValueChange={(v) => setWorkOrderForm({...workOrderForm, aircraft_id: v})}
              >
                <SelectTrigger className="bg-slate-800">
                  <SelectValue placeholder="Select aircraft" />
                </SelectTrigger>
                <SelectContent>
                  {aircraft.map(ac => (
                    <SelectItem key={ac.id} value={ac.id}>
                      {ac.registration} - {ac.aircraft_type || ac.type || 'Aircraft'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                placeholder="Work order title"
                value={workOrderForm.title}
                onChange={(e) => setWorkOrderForm({...workOrderForm, title: e.target.value})}
                className="bg-slate-800"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Work order description"
                value={workOrderForm.description}
                onChange={(e) => setWorkOrderForm({...workOrderForm, description: e.target.value})}
                className="bg-slate-800"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={workOrderForm.work_order_type} 
                  onValueChange={(v) => setWorkOrderForm({...workOrderForm, work_order_type: v})}
                >
                  <SelectTrigger className="bg-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="unscheduled">Unscheduled</SelectItem>
                    <SelectItem value="ad_compliance">AD Compliance</SelectItem>
                    <SelectItem value="sb_compliance">SB Compliance</SelectItem>
                    <SelectItem value="inspection">Inspection</SelectItem>
                    <SelectItem value="component_change">Component Change</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select 
                  value={workOrderForm.priority} 
                  onValueChange={(v) => setWorkOrderForm({...workOrderForm, priority: v})}
                >
                  <SelectTrigger className="bg-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aog">AOG - Immediate</SelectItem>
                    <SelectItem value="critical">Critical - 24h</SelectItem>
                    <SelectItem value="high">High - 72h</SelectItem>
                    <SelectItem value="routine">Routine</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWorkOrderDialog(false)}>Cancel</Button>
            <Button 
              onClick={createWorkOrder} 
              disabled={!workOrderForm.aircraft_id || !workOrderForm.title}
              className="bg-blue-500 hover:bg-blue-600"
            >
              <Plus className="w-4 h-4 mr-2" /> Create Work Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REPORT DEFECT DIALOG */}
      <Dialog open={showDefectDialog} onOpenChange={setShowDefectDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileWarning className="w-5 h-5 text-orange-400" />
              Report Defect
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Aircraft</Label>
              <Select 
                value={defectForm.aircraft_id} 
                onValueChange={(v) => setDefectForm({...defectForm, aircraft_id: v})}
              >
                <SelectTrigger className="bg-slate-800">
                  <SelectValue placeholder="Select aircraft" />
                </SelectTrigger>
                <SelectContent>
                  {aircraft.map(ac => (
                    <SelectItem key={ac.id} value={ac.id}>
                      {ac.registration} - {ac.aircraft_type || ac.type || 'Aircraft'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>ATA Chapter</Label>
                <Input
                  placeholder="e.g., 32"
                  value={defectForm.ata_chapter}
                  onChange={(e) => setDefectForm({...defectForm, ata_chapter: e.target.value})}
                  className="bg-slate-800"
                />
              </div>
              <div className="space-y-2">
                <Label>Severity</Label>
                <Select 
                  value={defectForm.severity} 
                  onValueChange={(v) => setDefectForm({...defectForm, severity: v})}
                >
                  <SelectTrigger className="bg-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aog">AOG</SelectItem>
                    <SelectItem value="safety">Safety</SelectItem>
                    <SelectItem value="major">Major</SelectItem>
                    <SelectItem value="minor">Minor</SelectItem>
                    <SelectItem value="cosmetic">Cosmetic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                placeholder="Brief description of defect"
                value={defectForm.title}
                onChange={(e) => setDefectForm({...defectForm, title: e.target.value})}
                className="bg-slate-800"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Detailed description of the defect..."
                value={defectForm.description}
                onChange={(e) => setDefectForm({...defectForm, description: e.target.value})}
                className="bg-slate-800"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Source</Label>
              <Select 
                value={defectForm.source} 
                onValueChange={(v) => setDefectForm({...defectForm, source: v})}
              >
                <SelectTrigger className="bg-slate-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pilot_report">Pilot Report</SelectItem>
                  <SelectItem value="cabin_crew">Cabin Crew</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="inspection">Inspection</SelectItem>
                  <SelectItem value="monitoring">Monitoring (FDM)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDefectDialog(false)}>Cancel</Button>
            <Button 
              onClick={createDefect} 
              disabled={!defectForm.aircraft_id || !defectForm.title || !defectForm.ata_chapter}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Plus className="w-4 h-4 mr-2" /> Report Defect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
